/**
 * MCTS Node for pure Monte Carlo Tree Search
 */

import { GameState, IMove, Position, PieceType } from "../../types";

export interface MoveInfo {
  fromPosition: Position;
  move: IMove;
}

export class MCTSNode {
  gameState: GameState;
  move: MoveInfo | null; // null for root node
  parent: MCTSNode | null;
  children: MCTSNode[];

  visits: number;
  wins: number;
  isFullyExpanded: boolean;
  untriedMoves: MoveInfo[];

  constructor(
    gameState: GameState,
    move: MoveInfo | null = null,
    parent: MCTSNode | null = null,
    untriedMoves: MoveInfo[] = []
  ) {
    this.gameState = gameState;
    this.move = move;
    this.parent = parent;
    this.children = [];
    this.visits = 0;
    this.wins = 0;
    this.isFullyExpanded = false;
    this.untriedMoves = [...untriedMoves];
  }

  /**
   * Calculate UCB1 value for node selection
   * UCB1 = (wins / visits) + C * sqrt(ln(parent.visits) / visits)
   */
  getUCB1(explorationParameter: number = 1.41): number {
    if (this.visits === 0) return Infinity;
    if (!this.parent) return this.wins / this.visits;

    const exploitation = this.wins / this.visits;
    const exploration =
      explorationParameter * Math.sqrt(Math.log(this.parent.visits) / this.visits);

    return exploitation + exploration;
  }

  /**
   * Select best child based on UCB1
   */
  selectBestChild(explorationParameter: number = 1.41): MCTSNode {
    return this.children.reduce((best, child) => {
      const bestUCB = best.getUCB1(explorationParameter);
      const childUCB = child.getUCB1(explorationParameter);
      return childUCB > bestUCB ? child : best;
    }, this.children[0]);
  }

  /**
   * Expand node by adding one new child
   */
  expand(newGameState: GameState): MCTSNode {
    if (this.untriedMoves.length === 0) {
      this.isFullyExpanded = true;
      throw new Error("No untried moves to expand");
    }

    // Take random untried move
    const moveIndex = Math.floor(Math.random() * this.untriedMoves.length);
    const move = this.untriedMoves.splice(moveIndex, 1)[0];

    // Create new child node
    const childNode = new MCTSNode(newGameState, move, this);
    this.children.push(childNode);

    if (this.untriedMoves.length === 0) {
      this.isFullyExpanded = true;
    }

    return childNode;
  }

  /**
   * Update node statistics after simulation
   */
  update(result: number): void {
    this.visits++;
    this.wins += result;
  }

  /**
   * Get best child by visit count (for final selection)
   */
  getBestChildByVisits(): MCTSNode {
    return this.children.reduce((best, child) => {
      return child.visits > best.visits ? child : best;
    }, this.children[0]);
  }

  /**
   * Get best child by win rate (alternative selection)
   */
  getBestChildByWinRate(): MCTSNode {
    return this.children.reduce((best, child) => {
      const bestWinRate = best.wins / best.visits;
      const childWinRate = child.wins / child.visits;
      return childWinRate > bestWinRate ? child : best;
    }, this.children[0]);
  }

  /**
   * Get win rate for this node
   */
  getWinRate(): number {
    return this.visits > 0 ? this.wins / this.visits : 0;
  }

  /**
   * Check if this is a terminal node
   */
  isTerminal(): boolean {
    return (
      this.gameState.state === "Post Game" ||
      this.gameState.state === "Draw" ||
      this.gameState.state === "Terminated"
    );
  }

  /**
   * Get all legal moves from current position
   */
  static getAllMoves(gameState: GameState): MoveInfo[] {
    const moves: MoveInfo[] = [];
    const bonusTurnPieceId = (gameState as any).activeBonusTurnPieceId;

    // Shim so movement strategies that branch on activeBonusTurnPieceId
    // (Penguin, Virus, Monkey) see the right context during MCTS search.
    // Without this they receive `undefined` and generate moves the engine
    // would reject — e.g. a penguin during its own bonus turn returning the
    // pre-slide moveset.
    const gameStateManagerShim = { getGameState: () => gameState };

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        const position: Position = { row, col };

        for (const piece of pieces) {
          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === gameState.isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if (!isOwnColor && !isZombified) continue;

          // Mirror MovementManager.getValidMoves status-effect guards so the
          // MCTS tree/rollouts never select moves the engine would reject.
          if (piece.poisonedStatus?.isPoisoned) continue;
          if (piece.isEntangled) continue;
          if (piece.type === PieceType.Tree && piece.isMeadowed) continue;
          if (piece.type === PieceType.Goat && piece.isFainted && pieces.length > 1) continue;
          if (bonusTurnPieceId && piece.id !== bonusTurnPieceId) continue;

          try {
            const validMoves = piece.getValidMovesForPiece(gameState.board, position, gameState.isLightTurn, gameStateManagerShim);
            for (const move of validMoves) {
              moves.push({ fromPosition: position, move });
            }
          } catch (error) {
            // Ignore invalid moves
          }
        }
      }
    }

    return moves;
  }
}